import { useCallback, useEffect, useRef, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { MATCHES, type MatchInfo, type MatchSnapshot } from "@/lib/matches";
import { unifiedSports } from "@/lib/sports/unifiedSportsService";
import type { ProviderMatchDetails } from "@/lib/sports/providers/types";
import { trackEvent } from "@/lib/audit";
import { t as translate, getStoredLang } from "@/lib/i18n";

export interface CrossMatchNotification {
  id: string;
  matchId: string;
  match: MatchInfo;
  snapshot: MatchSnapshot;
  title: string;
  subtitle: string;
  createdAt: number;
}

/**
 * Match hub: tracks the active match plus per-match snapshots for every secondary match.
 * Generates cross-match toasts when a notable event occurs in a non-active match.
 */
export interface MatchHubOptions {
  enabled?: boolean;
  followed?: string[];
  muted?: boolean;
  onActiveMatchChange?: (toId: string, fromId: string) => void;
}

/**
 * P0 Phase 5: snapshots are now sourced from unifiedSports (real provider with
 * cache + failover) instead of the local `snapshotFor` simulation. The
 * `globalElapsed` argument is kept for backward compatibility with consumers
 * that drive cosmetic animations, but no longer derives scores/minute.
 */
function detailsToSnapshot(
  id: string,
  details: ProviderMatchDetails | undefined,
): MatchSnapshot {
  if (!details) {
    return { id, homeScore: 0, awayScore: 0, minute: 0, status: "LIVE" };
  }
  const status: MatchSnapshot["status"] =
    details.status === "FINISHED" ? "FT" : "LIVE";
  return {
    id,
    homeScore: details.scoreHome ?? 0,
    awayScore: details.scoreAway ?? 0,
    minute: details.minute ?? 0,
    status,
  };
}

export function useMatchHub(_globalElapsed: number, opts: MatchHubOptions = {}) {
  const { enabled = true, followed = [], muted = false, onActiveMatchChange } = opts;
  const [activeId, setActiveId] = useState<string>(MATCHES[0].id);
  const [toasts, setToasts] = useState<CrossMatchNotification[]>([]);
  const seenEvents = useRef<Set<string>>(new Set());
  const lastGoals = useRef<Record<string, { home: number; away: number }>>({});

  // Per-match real data fetched via unifiedSports (cache + provider failover).
  // ~30s refetch matches the live-tick cadence of the prediction engine.
  const detailsResults = useQueries({
    queries: MATCHES.map((m) => {
      const detailsId = m.providerFixtureId != null ? String(m.providerFixtureId) : m.id;
      return {
      queryKey: ["match-hub", "details", m.id, detailsId] as const,
      queryFn: () => unifiedSports.getMatchDetails(detailsId),
      refetchInterval: 30_000,
      staleTime: 25_000,
      retry: 1,
      };
    }),
  });

  const snapshots: Record<string, MatchSnapshot> = {};
  for (let i = 0; i < MATCHES.length; i++) {
    const m = MATCHES[i];
    snapshots[m.id] = detailsToSnapshot(m.id, detailsResults[i]?.data);
  }

  // Cross-match goal detection driven by score deltas from real data.
  useEffect(() => {
    if (!enabled || muted) return;
    for (const m of MATCHES) {
      if (m.id === activeId) continue;
      if (!followed.includes(m.id)) continue;
      const snap = snapshots[m.id];
      if (!snap) continue;
      const prevGoals = lastGoals.current[m.id] ?? { home: 0, away: 0 };
      const goalScored =
        snap.homeScore > prevGoals.home
          ? "home"
          : snap.awayScore > prevGoals.away
          ? "away"
          : null;
      lastGoals.current[m.id] = { home: snap.homeScore, away: snap.awayScore };
      if (goalScored) {
        const key = `${m.id}:goal:${snap.homeScore}-${snap.awayScore}`;
        if (!seenEvents.current.has(key)) {
          seenEvents.current.add(key);
          const lang = getStoredLang();
          const title = translate("cross_match_goal_title", lang, {
            home: m.homeCode, away: m.awayCode, hs: snap.homeScore, as: snap.awayScore,
          });
          const subtitle = translate("cross_match_watch_now", lang);
          pushToast({ id: key, matchId: m.id, match: m, snapshot: snap, title, subtitle });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeId, enabled, muted, followed.join(","),
    ...MATCHES.map((m) => `${snapshots[m.id]?.homeScore}-${snapshots[m.id]?.awayScore}`),
  ]);

  const pushToast = (n: Omit<CrossMatchNotification, "createdAt">) => {
    const full: CrossMatchNotification = { ...n, createdAt: Date.now() };
    setToasts((prev) => [...prev.slice(-2), full]);
    void trackEvent("cross_match_toast_shown", { matchId: n.matchId, title: n.title });
  };

  // Auto-dismiss toasts after 6s
  useEffect(() => {
    if (!toasts.length) return;
    const timer = setTimeout(() => {
      setToasts((prev) => prev.slice(1));
    }, 6000);
    return () => clearTimeout(timer);
  }, [toasts]);

  const switchMatch = useCallback(
    (id: string, source: "global_bar" | "toast" = "global_bar") => {
      if (id === activeId) return;
      const fromId = activeId;
      setActiveId(id);
      // Clear all pending toasts / seen events from previous match
      setToasts([]);
      seenEvents.current = new Set();
      void trackEvent("match_switched", { to: id, source });
      void trackEvent("active_match_changed", { from: fromId, to: id, source });
      onActiveMatchChange?.(id, fromId);
    },
    [activeId, onActiveMatchChange],
  );

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const openToast = useCallback(
    (n: CrossMatchNotification) => {
      void trackEvent("cross_match_toast_clicked", { matchId: n.matchId });
      switchMatch(n.matchId, "toast");
      dismissToast(n.id);
    },
    [switchMatch, dismissToast],
  );

  return {
    matches: MATCHES,
    snapshots,
    activeId,
    activeMatch: MATCHES.find((m) => m.id === activeId) ?? MATCHES[0],
    switchMatch,
    toasts,
    openToast,
    dismissToast,
  };
}
